import { SEO } from "@/components/SEO";

import { useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Printer, PenTool, Scissors, Palette } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

function AnimatedDiv({ children, className = "" }) {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeUp} className={className}>
      {children}
    </motion.div>
  );
}

const capabilities = [
  {
    title: "Digital Printing Infrastructure",
    desc: "State-of-the-art digital printing machines delivering vibrant, precise patterns with fast turnaround and unlimited color possibilities.",
    icon: Printer,
    image: "https://images.unsplash.com/photo-1739863305972-4eea3851e521?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHx0ZXh0aWxlJTIwcHJpbnRpbmclMjBmYWN0b3J5JTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3NzM3NDMwODJ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Embroidery Units",
    desc: "Advanced multi-head embroidery machines capable of intricate designs, sequin work, and thread embroidery at scale.",
    icon: PenTool,
    image: "https://images.unsplash.com/photo-1663782464052-d4f9a77b24a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHx0ZXh0aWxlJTIwZmFicmljJTIwZW1icm9pZGVyeSUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzczNzQzMDc3fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Stitching Facilities",
    desc: "Full-service stitching units with skilled tailors and modern equipment for high-volume garment production.",
    icon: Scissors,
    image: "https://images.unsplash.com/photo-1663888673897-f8bc14482f17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHx0ZXh0aWxlJTIwZmFicmljJTIwZW1icm9pZGVyeSUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzczNzQzMDc3fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    title: "Design Development",
    desc: "In-house design team creating over 10,000+ original designs annually, combining trend awareness with market insight.",
    icon: Palette,
    image: "https://images.unsplash.com/photo-1607300110843-b3994a493d98?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBmYWJyaWMlMjB0ZXh0dXJlJTIwY2xvc2UlMjB1cCUyMHNpbGslMjBjb3R0b258ZW58MHx8fHwxNzczNzQyOTk3fDA&ixlib=rb-4.1.0&q=85",
  },
];

export default function CapabilitiesPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="capabilities-page" className="pt-20 lg:pt-24">
      <SEO
        title="Manufacturing Capabilities | Bulk Textile Production & Export — KF Prints Surat"
        description="Vertically integrated textile manufacturing in Surat, India: printing, processing, finishing and bulk production with export-compliant quality systems and flexible MOQs for international buyers."
        keywords="textile manufacturing capabilities India, bulk fabric production, fabric printing Surat, textile export capabilities, flexible MOQ fabric manufacturer"
        path="/capabilities"
      />
      {/* Hero */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="section-divider mb-8" />
          <h1 className="text-5xl md:text-7xl font-serif tracking-tight text-foreground mb-6">Our Capabilities</h1>
          <p className="text-base md:text-lg font-sans text-muted-foreground max-w-xl leading-relaxed">
            Engineered for both scale and precision.
          </p>
        </div>
      </section>

      {/* Capabilities Grid */}
      {capabilities.map((cap, index) => (
        <section key={cap.title} className={`py-20 lg:py-28 ${index % 2 === 0 ? 'bg-background' : 'bg-white'}`}>
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center`}>
              <AnimatedDiv className={index % 2 !== 0 ? 'lg:order-2' : ''}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={cap.image} alt={cap.title} className="w-full h-full object-cover" />
                </div>
              </AnimatedDiv>
              <AnimatedDiv className={index % 2 !== 0 ? 'lg:order-1' : ''}>
                <cap.icon size={36} className="text-[#C5A059] mb-6" strokeWidth={1.5} />
                <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-foreground mb-4">{cap.title}</h2>
                <p className="text-base font-sans text-muted-foreground leading-relaxed">{cap.desc}</p>
              </AnimatedDiv>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
